/**
 * /settings — System Settings editor (Story 7.9).
 *
 * Surface:
 *   - Tabs per category (openai, quality, opt_out, callback, lgpd, batch, etc.)
 *     derived from the `byCategory` object returned by the API.
 *   - Each tab: list of {@link SettingInput} with its own save button.
 *   - PUT is admin-only server-side; UI surfaces a warning banner for
 *     non-admins so they understand why the save buttons 403 if they try.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/sonner';
import { ApiError, apiClient } from '@/lib/api';
import {
  SettingsCategoryPanel,
  type SettingDto,
} from '@/components/settings/SettingsCategoryPanel';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';

interface SettingsResponse {
  data: SettingDto[];
  byCategory: Record<string, SettingDto[]>;
}

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { code?: string; message?: string; field?: string } };
    const code = body?.error?.code;
    if (code === 'VALIDATION_FAILED') {
      const field = body.error?.field ? ` (${body.error.field})` : '';
      return `${body.error?.message ?? 'Validação falhou.'}${field}`;
    }
    if (code === 'SETTING_NOT_FOUND') return 'Configuração não encontrada.';
    if (code === 'INSUFFICIENT_ROLE') {
      return 'Apenas administradores podem alterar configurações.';
    }
    if (body?.error?.message) return body.error.message;
    return `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function SettingsPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';

  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['system-settings'],
    queryFn: () => apiClient.get<SettingsResponse>('/admin/settings'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      apiClient.put<SettingDto>(`/admin/settings/${encodeURIComponent(key)}`, { value }),
    onMutate: ({ key }) => {
      setSavingKey(key);
    },
    onSettled: () => setSavingKey(null),
    onSuccess: () => {
      toast.success('Configuração salva.');
      void queryClient.invalidateQueries({ queryKey: ['system-settings'] });
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao salvar configuração.')),
  });

  const categories = useMemo(() => {
    if (!listQuery.data) return [];
    return Object.keys(listQuery.data.byCategory).sort();
  }, [listQuery.data]);

  const current = activeCategory ?? categories[0] ?? null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Parâmetros globais do sistema (Configuration Matrix). Secrets são mascarados na leitura e
          no audit trail.
        </p>
      </div>

      {!isAdmin ? (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          Você está em modo somente-leitura. Apenas administradores podem alterar configurações.
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Configurações</CardTitle>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <div className="py-6 text-sm text-muted-foreground">Carregando…</div>
          ) : listQuery.error ? (
            <div className="py-6 text-sm text-destructive">
              {humanizeError(listQuery.error, 'Falha ao carregar configurações.')}
            </div>
          ) : categories.length === 0 ? (
            <div className="py-6 text-sm text-muted-foreground">
              Nenhuma configuração cadastrada.
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div
                role="tablist"
                aria-label="Categorias de settings"
                className="flex gap-2 overflow-x-auto border-b pb-2 sm:flex-wrap"
              >
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    role="tab"
                    aria-selected={cat === current}
                    onClick={() => setActiveCategory(cat)}
                    className={cn(
                      'shrink-0 truncate rounded-md px-3 py-1.5 text-xs font-medium transition-colors sm:text-sm',
                      cat === current
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                    )}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <div role="tabpanel" aria-label={current ?? ''}>
                {current ? (
                  <SettingsCategoryPanel
                    settings={listQuery.data!.byCategory[current] ?? []}
                    disabled={!isAdmin}
                    savingKey={savingKey}
                    onSave={(key, value) => updateMutation.mutate({ key, value })}
                  />
                ) : null}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default SettingsPage;
